import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Trophy, Medal, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Leaderboard = () => {
  const navigate = useNavigate();
  const [globalLeaders, setGlobalLeaders] = useState([]);
  const [gameLeaders, setGameLeaders] = useState([]);
  const [selectedGame, setSelectedGame] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGlobalLeaderboard();
  }, []);

  useEffect(() => {
    if (selectedGame !== 'global') {
      fetchGameLeaderboard();
    }
  }, [selectedGame]);

  const fetchGlobalLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard/global`);
      setGlobalLeaders(response.data);
    } catch (error) {
      console.error('Failed to fetch global leaderboard', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGameLeaderboard = async () => {
    try {
      const params = selectedGame !== 'all' ? `?game_type=${selectedGame}` : '';
      const response = await axios.get(`${API}/leaderboard${params}`);
      setGameLeaders(response.data);
    } catch (error) {
      console.error('Failed to fetch game leaderboard', error);
    }
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (index === 1) return <Medal className="w-6 h-6 text-zinc-400" />;
    if (index === 2) return <Award className="w-6 h-6 text-orange-600" />;
    return <span className="text-zinc-500 font-mono text-sm">#{index + 1}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard-button"
          className="mb-8 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-12" data-testid="leaderboard-header">
          <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Rankings</span>
          <h1 className="text-4xl md:text-5xl font-grotesk font-bold text-white mt-2 mb-4 tracking-tight">
            Top <span className="text-primary">Performers</span>
          </h1>
          <p className="text-zinc-400">See how you rank against other blockchain learners</p>
        </div>

        <Tabs defaultValue="global" className="w-full" onValueChange={setSelectedGame}>
          <TabsList className="bg-zinc-900/30 border border-white/10 mb-8">
            <TabsTrigger value="global" data-testid="tab-global">Global XP</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all-games">All Games</TabsTrigger>
            <TabsTrigger value="matching" data-testid="tab-matching">Matching</TabsTrigger>
            <TabsTrigger value="quiz-battle" data-testid="tab-quiz">Quiz Battle</TabsTrigger>
          </TabsList>

          <TabsContent value="global" data-testid="global-leaderboard">
            <div className="space-y-3">
              {globalLeaders.map((leader, index) => (
                <div
                  key={leader.id}
                  data-testid={`leader-${index}`}
                  className="bg-zinc-900/30 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-primary/30"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 flex items-center justify-center">
                      {getRankIcon(index)}
                    </div>
                    <div>
                      <div className="text-white font-grotesk font-semibold">{leader.username}</div>
                      <div className="text-xs text-zinc-500 font-mono uppercase">{leader.level}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-mono font-bold text-primary">{leader.xp}</div>
                    <div className="text-xs text-zinc-500">XP</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="all" data-testid="all-games-leaderboard">
            <div className="space-y-3">
              {gameLeaders.map((leader, index) => (
                <div
                  key={leader.id}
                  data-testid={`game-leader-${index}`}
                  className="bg-zinc-900/30 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-primary/30"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 flex items-center justify-center">
                      {getRankIcon(index)}
                    </div>
                    <div>
                      <div className="text-white font-grotesk font-semibold">{leader.username}</div>
                      <div className="text-xs text-zinc-500">{leader.game_type}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-mono font-bold text-primary">{leader.score}</div>
                    <div className="text-xs text-zinc-500">{leader.time_taken}s</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="matching" data-testid="matching-leaderboard">
            <div className="space-y-3">
              {gameLeaders.map((leader, index) => (
                <div
                  key={leader.id}
                  className="bg-zinc-900/30 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-primary/30"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 flex items-center justify-center">
                      {getRankIcon(index)}
                    </div>
                    <div className="text-white font-grotesk font-semibold">{leader.username}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-mono font-bold text-primary">{leader.score}</div>
                    <div className="text-xs text-zinc-500">{leader.time_taken}s</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="quiz-battle" data-testid="quiz-leaderboard">
            <div className="space-y-3">
              {gameLeaders.map((leader, index) => (
                <div
                  key={leader.id}
                  className="bg-zinc-900/30 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-primary/30"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 flex items-center justify-center">
                      {getRankIcon(index)}
                    </div>
                    <div className="text-white font-grotesk font-semibold">{leader.username}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-mono font-bold text-primary">{leader.score}</div>
                    <div className="text-xs text-zinc-500">{leader.time_taken}s</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Leaderboard;
